import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { useAuth } from "../lib/authContext";
import {
  canUsePush, enablePush, disablePush, pushApi, getCurrentPushSubscription,
} from "../lib/pushApi";
import { toast } from "sonner";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

/**
 * Card on /jornada that lets logged-in users opt in to the daily 7am push
 * reminder. Hidden for anonymous users. Also handles iOS PWA edge case
 * (must be installed to home screen first on iOS 16.4+).
 */
export default function PushOptInCard() {
  const { isAuthenticated } = useAuth();

  const [supported] = useState(canUsePush());
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [perm, setPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !supported) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sub = await getCurrentPushSubscription();
        if (cancelled) return;
        if (sub) {
          // Verify backend agrees
          try {
            const prefs = await pushApi.preferences();
            if (!cancelled) setEnabled(!!prefs.enabled);
          } catch {
            if (!cancelled) setEnabled(true);
          }
        } else {
          setEnabled(false);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, supported]);

  if (!isAuthenticated) return null;

  const iosBlocked = isIOS() && !isStandalone();

  const handleEnable = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await enablePush();
      setEnabled(true);
      setPerm("granted");
      toast.success("Notificações ativadas!");
    } catch (e) {
      toast.error(e?.message || "Não foi possível ativar.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await disablePush();
      setEnabled(false);
      toast.success("Notificações desativadas.");
    } catch {
      toast.error("Não foi possível desativar.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await pushApi.test();
      toast.success("Notificação de teste enviada.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha no teste.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Render
  return (
    <section
      className="mt-6 bg-white border border-border rounded-2xl p-5"
      data-testid="push-optin-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
          {enabled ? <Bell className="w-4 h-4 text-gold" /> : <BellOff className="w-4 h-4 text-navy/55" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Reflexão diária</p>
          <h2 className="font-display text-xl text-navy mt-1.5 leading-tight">
            {enabled ? "Você receberá às 7h da manhã" : "Receba uma reflexão todos os dias"}
          </h2>
          <p className="text-[13px] text-navy/65 mt-2 leading-relaxed">
            Uma notificação simples às 7h: <em>"Sua reflexão chegou!"</em> com link direto para o devocional do dia.
          </p>

          {!supported && (
            <p className="mt-3 text-[12px] text-red-600/80" data-testid="push-unsupported">
              Seu navegador não suporta notificações push.
            </p>
          )}

          {supported && iosBlocked && (
            <p className="mt-3 text-[12px] text-amber-700" data-testid="push-ios-hint">
              No iPhone, primeiro toque em Compartilhar → "Adicionar à Tela de Início" e abra pelo ícone para ativar notificações (iOS 16.4+).
            </p>
          )}

          {supported && !iosBlocked && perm === "denied" && (
            <p className="mt-3 text-[12px] text-amber-700" data-testid="push-denied-hint">
              Você bloqueou notificações deste site. Abra as configurações do navegador para reabilitar.
            </p>
          )}

          {supported && !iosBlocked && perm !== "denied" && !checking && (
            <div className="mt-4 flex flex-wrap gap-2">
              {!enabled ? (
                <button
                  type="button"
                  onClick={handleEnable}
                  disabled={loading}
                  data-testid="push-enable"
                  className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                  Ativar lembrete diário
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={loading}
                    data-testid="push-test"
                    className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#d4b876] disabled:opacity-50 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Enviar teste
                  </button>
                  <button
                    type="button"
                    onClick={handleDisable}
                    disabled={loading}
                    data-testid="push-disable"
                    className="inline-flex items-center gap-2 bg-white border border-border text-navy hover:border-gold/50 disabled:opacity-50 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellOff className="w-3.5 h-3.5" />}
                    Desativar
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
